import { initializeOfflineStorage, getEffectiveStorageEngine } from './offlineStorageAdapter.js';
import { recoverExpiredInFlightOperations } from './visitaOfflineOperationQueue.js';
import { inspectLegacyOfflineInventory } from './offlineLegacyInventory.js';
import { migrateLegacyVisitaContexts } from './visitaContextLegacy.js';
import { recoverOfflineImageFiles } from './visitaOfflineMediaQueue.js';
import { isNativePlatform } from '../mobile/capacitorPlatform.js';
import {inspectWebLegacyReadiness} from './webLegacyReadiness.js';

export class OfflineStartupError extends Error {
    constructor(code) {
        super(code === 'legacy_review' ? 'Hay datos de una versión anterior que requieren recuperación.' : 'No se pudo verificar el almacenamiento local.');
        this.code = code;
    }
}

// Una sola preparación por runtime. Un error no se memoriza como éxito ni habilita la UI.
export function createOfflineStartup({ initialize = initializeOfflineStorage, status = getEffectiveStorageEngine,
    native = isNativePlatform, inventory = inspectLegacyOfflineInventory,
    migrateContexts = migrateLegacyVisitaContexts, recover = recoverExpiredInFlightOperations, recoverFiles = recoverOfflineImageFiles,
    webInventory = inspectWebLegacyReadiness } = {}) {
    let pending = null, ready = null;
    return {
        start() {
            if (ready) return Promise.resolve(ready);
            if (pending) return pending;
            pending = (async () => {
                const isNative = native();
                const driver = await initialize(isNative ? { assertEngine: 'sqlite' } : {});
                const engine = status();
                if (engine.isFallback || engine.engine !== (isNative ? 'sqlite' : 'indexeddb')) throw new OfflineStartupError('engine');
                if (isNative) {
                    for (const method of ['query', 'run', 'execute', 'executeSet']) {
                        if (typeof driver.nativeConnector?.[method] !== 'function') throw new OfflineStartupError('capability');
                    }
                    const integrity = await driver.nativeConnector.query('PRAGMA quick_check', []);
                    if (integrity?.values?.length !== 1 || integrity.values[0].quick_check !== 'ok')
                        throw new OfflineStartupError('integrity');
                    const legacy = await inventory({ driver });
                    if (legacy.complete !== true || legacy.knownPending !== 0 || legacy.ambiguousOwners !== 0) throw new OfflineStartupError('legacy_review');
                    await migrateContexts({ driver, native: true });
                } else {
                    const legacy = await webInventory({driver});
                    if (legacy.complete !== true || legacy.knownPending !== 0) throw new OfflineStartupError('legacy_review');
                }
                await recover(); // CAS por fila; conserva UUID/payload y concesiones renovadas por otro ejecutor.
                const files = await recoverFiles({ driver });
                if (!files || files.needsReview !== 0) throw new OfflineStartupError('files_review');
                ready = { engine: engine.engine };
                return ready;
            })().finally(() => { pending = null; });
            return pending;
        },
    };
}

export const offlineStartup = createOfflineStartup();
